/**
 * Audio Description Player
 * Props are updated based on changes from the MediaPlayer and Transcript
 */

import { h } from "preact";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
} from "preact/hooks";
import { useRecordedURL } from "../../helpers/routes";
import SettingsContext from "../../SettingsContext";

type ADPlayerTypes = {
  playing?: boolean;
  muted: boolean;
  seekTo?: number;
  playbackRate: number;
  onSeek?: () => void;
};

const ADPlayer = ({ playing, muted, seekTo, playbackRate }: ADPlayerTypes) => {
  const player = useRef<HTMLMediaElement>(null); // local ref to interact with the internal player
  const { settings } = useContext(SettingsContext);
  const url = useRecordedURL("/audio_description");

  const play = useCallback(async (): Promise<void> => {
    await player.current?.play();
  }, [player]);

  const pause = useCallback((): void => {
    player.current?.pause();
  }, [player]);

  const seek = useCallback(
    (seekTo: number): void => {
      if (player.current) {
        player.current.currentTime = seekTo;
      }
    },
    [player]
  );

  const rate = useCallback(
    (playbackRate: number): void => {
      if (player.current) {
        player.current.playbackRate = playbackRate;
      }
    },
    [player]
  );

  // Changes in props, call methods on the player
  useEffect(() => {
    if (playing === true) {
      play().catch((e) => {
        console.warn("Play failed: ", e);
      });
    } else if (playing === false) {
      pause();
    }
  }, [playing, play, pause]);

  useEffect(() => {
    if (seekTo) seek(seekTo);
  }, [seekTo, seek]);

  useLayoutEffect(() => {
    if (playbackRate) rate(playbackRate);
  }, [playbackRate, rate]);

  return (
    <audio
      ref={player}
      src={url.toString()}
      muted={muted}
      data-testid="ad-player"
    >
      {/* TODO: lang label and srcLang should be dynamic */}
      <track src="" kind="captions" srcLang="en" label="English" />
    </audio>
  );
};

export default ADPlayer;
